<template>
  <div class="plot-header">Payments by Day</div>
  <i
    style="position: absolute; right: 10px; top: 9px; color: white"
    class="fas fa-close"
  ></i>
  <div ref="plot" class="plot-container">
    <div v-if="!barChartData.length" class="loading-message">
      No data available to display.
    </div>
  </div>
</template>

<script>
import Plotly from 'plotly.js-dist';
import { nextTick } from 'vue';

export default {
  name: 'PlotReceipts',
  props: {
    barChartData: {
      type: Array,
      required: true,
    },
  },
  watch: {
    barChartData: {
      handler() {
        if (this.barChartData.length) {
          nextTick(() => {
            this.plotReceipts();
          });
        }
      },
      immediate: true,
    },
  },
  methods: {
    formatToK(value) {
      return (
        ' ' +
        (value >= 1000 ? `${(value / 1000).toFixed(1)}K` : value.toFixed(2))
      );
    },
    plotReceipts() {
      if (!this.$refs.plot) {
        console.error('Plot container is not available.');
        return;
      }
      // console.log('barChartData', this.barChartData);

      const xValues = this.barChartData.map((item) => {
        const date = new Date(item.date);
        return `${String(date.getMonth() + 1).padStart(2, '0')}/${String(date.getDate()).padStart(2, '0')}`;
      });

      // Separating the y-values based on type
      const paymentYValues = this.barChartData.map((item) =>
        item.type === 'payment' ? Number(item.totalPaid) || 0 : 0,
      );
      const bankYValues = this.barChartData.map((item) =>
        item.type === 'bank' ? Number(item.totalPaid) || 0 : 0,
      );

      // console.log('xValues', xValues);
      // console.log('paymentYValues', paymentYValues);
      // console.log('bankYValues', bankYValues);

      const runningTotalPaid = this.barChartData.map(
        (item) => Number(item.runningTotalPaid) || 0,
      );
      const runningPercentPaid = this.barChartData.map(
        (item) => parseFloat(item.runningPercentPaid) || 0,
      );

      const paymentBarTrace = {
        x: xValues,
        y: paymentYValues,
        name: 'Payments',
        type: 'bar',
        text: paymentYValues.map(
          (value, index) =>
            `${this.formatToK(value)} of ${this.formatToK(runningTotalPaid[index])}`,
        ),
        textposition: 'inside',
        hovertemplate:
          'Date: %{x}<br>Daily Total Paid: $%{y:,.2f}<br>Running Total Paid: %{text}<br>Running Percent Paid: %{customdata}',
        customdata: runningPercentPaid.map((value) => `${value.toFixed(2)}%`),
        marker: {
          color: '#baa2bd',
          line: {
            color: '#907793',
            width: 1.5,
          },
        },
      };

      const bankBarTrace = {
        x: xValues,
        y: bankYValues,
        name: 'Bank Transactions',
        type: 'bar',
        text: bankYValues.map(
          (value, index) =>
            `${this.formatToK(value)} of ${this.formatToK(runningTotalPaid[index])}`,
        ),
        textposition: 'inside',
        hovertemplate:
          'Date: %{x}<br>Daily Total Deposited:<br>$%{y:,.2f}<br>Running Total %{customdata}',
        customdata: runningTotalPaid.map((value) => this.formatToK(value)),
        marker: {
          color: '#8a6b9b',
          line: {
            color: '#644766',
            width: 1.5,
          },
        },
      };

      // Create annotations for percentage labels with better positioning
      let annotations = xValues
        .map((x, i) => {
          if (
            this.barChartData[i].type === 'payment' &&
            paymentYValues[i] > 0
          ) {
            // Adjusting x-position for grouped bars
            let xAdjust = -0.27; // This value might need tweaking based on visual inspection
            return {
              x: xValues[i],
              xref: 'x',
              y:
                paymentYValues[i] +
                Math.max(paymentYValues[i], bankYValues[i]) * 0.05,
              yref: 'y',
              text: `${runningPercentPaid[i].toFixed(1)} %`,
              showarrow: false,
              textangle: 90,
              font: {
                size: 11,
                color: '#333',
              },
              xanchor: 'center',
              yanchor: 'bottom',
              // Shift the annotation to the left to align with the payment bar
              xshift: (xAdjust * this.$refs.plot.offsetWidth) / xValues.length,
            };
          }
          return null;
        })
        .filter(Boolean);

      const layout = {
        margin: { t: 60, l: 40, r: 20, b: 60 }, // Increased top margin for legend space
        height: 600,
        hoverlabel: {
          font: {
            size: 12,
            color: '#ffffff',
          },
          bgcolor: '#333333',
        },
        font: {
          size: 12,
          family: 'Arial, sans-serif',
        },
        xaxis: {
          title: 'Date',
          tickangle: -90,
          automargin: true,
          tickformat: '%m/%d',
        },
        yaxis: {
          title: 'Total Paid',
          automargin: true,
        },
        barmode: 'group',
        annotations: annotations,
        // Adding legend configuration
        legend: {
          orientation: 'h', // Horizontal orientation
          y: 1.1, // Positioning above the plot
          yanchor: 'top',
          xanchor: 'center',
          x: 0.5, // Centered horizontally
        },
      };

      Plotly.newPlot(this.$refs.plot, [paymentBarTrace, bankBarTrace], layout, {
        displayModeBar: false,
      });
    },
  },
};
</script>

<style scoped>
.plot-container {
  width: 100%;
  height: 600px;
  border: 1px solid #ddd;
}
.plot-header {
  margin-bottom: 10px;
  font-size: 1em;
  font-weight: bold;
}
.loading-message {
  padding: 10px;
  text-align: center;
}
.plot-header {
  background: #333;
  color: #ddd;
  padding: 8px;
  border-top-left-radius: 5px;
  border-top-right-radius: 5px;
  margin: -10px;
  margin-bottom: 10px;
}
</style>
